import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  BookOpen,
  Clock,
  Sparkles,
  Mail,
  Instagram,
  Heart,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SubscribeForm from "@/components/SubscribeForm";
import SEO from "@/components/SEO";
import { ABOUT, BRAND } from "@/lib/content";

const FALLBACK_CATEGORIES = [
  "All",
  "Strength Training",
  "Women's Health",
  "Motherhood & Fitness",
  "Nutrition",
  "Mindset",
  "Lifestyle",
];

const POPULAR_SLUGS = [
  "why-women-should-lift-heavy",
  "how-to-build-confidence-in-the-gym",
  "how-to-rebuild-strength-after-having-children",
];

const TOPIC_PILLARS = [
  { label: "Strength Training", icon: TrendingUp },
  { label: "Women's Health", icon: Heart },
  { label: "Postpartum Coaching", icon: Sparkles },
  { label: "Gym Confidence", icon: BookOpen },
];

/* ---------- helpers ---------- */
function readingTime(content = "") {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/* ---------- Page ---------- */
export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog/categories")
      .then((r) => setCategories(["All", ...(r.data.categories || [])]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/blog/posts", { params: { category: active } })
      .then((r) => setPosts(r.data))
      .catch(() => toast.error("Could not load posts"))
      .finally(() => setLoading(false));
  }, [active]);

  /* derived collections */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
    );
  }, [posts, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);
  const popular = posts.filter((p) => POPULAR_SLUGS.includes(p.slug)).slice(0, 3);
  const showingAll = active === "All" && !query;

  return (
    <div data-testid="blog-page" className="bg-[#F6EFE9]">
      <SEO
        title="HerFitHub Journal | Women's Strength, Wellness & Coaching Magazine"
        description="A premium editorial journal on women's strength training, women's health, postpartum coaching and gym confidence — written by London-based women's strength coach Maya."
        path="/blog"
        keywords="women's strength training, women's health, women's fitness coach London, strength training for women, postpartum strength coaching, gym confidence for women, online coaching for women, women's wellness"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "HerFitHub Journal",
            url: "https://herfithub.com/blog",
            description:
              "Editorial wellness journal on women's strength training, women's health, postpartum coaching and gym confidence.",
            publisher: {
              "@type": "Organization",
              name: "HerFitHub",
              url: "https://herfithub.com",
            },
          },
        ]}
      />
      <Hero query={query} setQuery={setQuery} />
      <TopicPillars />
      {showingAll && featured && <FeaturedArticle post={featured} />}
      <CategoryFilter
        categories={categories}
        active={active}
        setActive={setActive}
      />
      {showingAll && popular.length >= 3 && <MostPopular posts={popular} />}
      <ArticlesGrid
        posts={showingAll && featured ? rest : filtered}
        loading={loading}
        query={query}
      />
      <AuthorCard />
      <NewsletterSection />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero({ query, setQuery }) {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
        <div className="max-w-3xl mx-auto text-center fade-in">
          <p className="eyebrow mb-5" data-testid="blog-eyebrow">
            The HerFitHub Journal · Strength · Wellness · Mindset
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.04] text-[#3A3A3A]">
            Strength built{" "}
            <span className="italic text-[#E8A6B8]">word by word</span>
          </h1>
          <p className="heart-divider mt-5">♡</p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-2xl mx-auto">
            A warm, editorial home for everything Maya teaches her clients —
            women's strength training, women's health, postpartum coaching,
            gym confidence and the realistic mindset shifts that make it all
            sustainable.
          </p>

          {/* Search */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-10 max-w-xl mx-auto"
            role="search"
            aria-label="Search articles"
          >
            <div className="relative">
              <Search
                size={18}
                strokeWidth={1.6}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-[#5C5C5C]"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the journal — e.g. squat, postpartum, confidence…"
                className="w-full pl-12 pr-5 py-4 rounded-full bg-white border border-[#F3C9D3]/70 text-sm text-[#3A3A3A] placeholder:text-[#5C5C5C]/70 focus:outline-none focus:border-[#E8A6B8] focus:shadow-[0_0_0_4px_rgba(232,166,184,0.18)] transition"
                data-testid="blog-search-input"
                aria-label="Search articles"
              />
            </div>
          </form>
        </div>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#E8A6B8]/15 blur-3xl"
      />
    </section>
  );
}

/* ---------- TOPIC PILLARS ---------- */
function TopicPillars() {
  return (
    <section aria-label="Topics covered" className="bg-white border-y border-[#F3C9D3]/40">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-7 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        {TOPIC_PILLARS.map((t) => {
          const I = t.icon;
          return (
            <div
              key={t.label}
              className="flex items-center justify-center gap-2 text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] text-[#3A3A3A]"
            >
              <I size={15} strokeWidth={1.6} className="text-[#E8A6B8]" />
              <span className="font-medium">{t.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- FEATURED ARTICLE ---------- */
function FeaturedArticle({ post }) {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <p className="eyebrow mb-2">Editor's Pick</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A]">
              Featured <span className="italic text-[#E8A6B8]">article</span>
            </h2>
          </div>
        </div>
        <Link
          to={`/blog/${post.slug}`}
          className="group grid lg:grid-cols-12 gap-8 lg:gap-12 items-center bg-white rounded-[2rem] overflow-hidden border border-[#F3C9D3]/40 transition-all duration-500 hover:shadow-[0_24px_60px_rgba(232,166,184,0.2)]"
          data-testid="blog-featured-article"
        >
          <div className="lg:col-span-7 aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden bg-[#F3C9D3]/20">
            <img
              src={post.image_url}
              alt={`${post.title} — ${post.category} · HerFitHub Journal`}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="lg:col-span-5 p-8 sm:p-10 lg:pr-12">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#E8A6B8]">
              {post.category}
            </p>
            <h3 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#3A3A3A] leading-tight">
              {post.title}
            </h3>
            <p className="mt-5 text-sm sm:text-base text-[#5C5C5C] leading-relaxed line-clamp-4">
              {post.excerpt}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.16em] text-[#5C5C5C]">
              <span className="flex items-center gap-2">
                <Clock size={12} className="text-[#E8A6B8]" /> {readingTime(post.content)} min read
              </span>
              <span className="text-[#E8A6B8]">•</span>
              <span>{formatDate(post.published_at)}</span>
            </div>
            <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] group-hover:text-[#E8A6B8] transition">
              Read The Article
              <ArrowRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
}

/* ---------- CATEGORY FILTER ---------- */
function CategoryFilter({ categories, active, setActive }) {
  return (
    <section className="py-10 bg-[#F3C9D3]/20" aria-label="Filter by category">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap justify-center gap-2.5">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`blog-cat-${c.toLowerCase().replace(/[\s&']+/g, "-")}`}
              aria-pressed={active === c}
              className={`px-5 py-2.5 rounded-full text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] font-semibold transition-all border ${
                active === c
                  ? "bg-[#3A3A3A] text-white border-[#3A3A3A] shadow-[0_8px_22px_rgba(58,58,58,0.18)]"
                  : "bg-white text-[#3A3A3A] border-[#F3C9D3] hover:border-[#E8A6B8] hover:-translate-y-0.5"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- MOST POPULAR ---------- */
function MostPopular({ posts }) {
  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow mb-2">Reader Favourites</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A]">
              Most <span className="italic text-[#E8A6B8]">popular</span>
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {posts.map((p, i) => (
            <ArticleCard key={p.id} post={p} rank={i + 1} compact />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- ARTICLES GRID ---------- */
function ArticlesGrid({ posts, loading, query }) {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow mb-2">Latest From The Journal</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A]">
              {query ? (
                <>Results for <span className="italic text-[#E8A6B8]">"{query}"</span></>
              ) : (
                <>All <span className="italic text-[#E8A6B8]">articles</span></>
              )}
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white rounded-[2rem] border border-[#F3C9D3]/30 overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-[#F3C9D3]/30" />
                <div className="p-7 space-y-4">
                  <div className="h-3 w-24 bg-[#F3C9D3]/40 rounded" />
                  <div className="h-6 w-3/4 bg-[#F3C9D3]/40 rounded" />
                  <div className="h-3 w-full bg-[#F3C9D3]/30 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <EmptyState query={query} />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {posts.map((p) => (
              <ArticleCard key={p.id} post={p} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ArticleCard({ post, rank, compact }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group bg-white rounded-[2rem] overflow-hidden border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(232,166,184,0.2)] flex flex-col"
      data-testid={`blog-post-${post.slug}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F3C9D3]/20">
        <img
          src={post.image_url}
          alt={`${post.title} — ${post.category}`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {rank ? (
          <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-full w-10 h-10 inline-flex items-center justify-center font-serif-display text-lg text-[#3A3A3A] border border-white/70 shadow-sm">
            {String(rank).padStart(2, "0")}
          </span>
        ) : (
          <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] border border-white/70">
            {post.category}
          </span>
        )}
      </div>
      <div className={`p-7 flex-1 flex flex-col ${compact ? "" : ""}`}>
        {rank && (
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#E8A6B8] font-semibold mb-2">
            {post.category}
          </p>
        )}
        <h3 className="font-serif-display text-2xl text-[#3A3A3A] leading-snug">
          {post.title}
        </h3>
        <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed line-clamp-3 flex-1">
          {post.excerpt}
        </p>
        <div className="mt-5 flex items-center justify-between gap-3 pt-5 border-t border-[#F3C9D3]/30">
          <span className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] text-[#5C5C5C]">
            <Clock size={12} className="text-[#E8A6B8]" /> {readingTime(post.content)} min read
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] group-hover:text-[#E8A6B8] transition-colors">
            Read
            <ArrowRight
              size={12}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </span>
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ query }) {
  return (
    <div
      className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 p-12 sm:p-16 text-center max-w-2xl mx-auto"
      data-testid="blog-empty-state"
    >
      <div className="inline-flex p-4 rounded-full bg-[#F3C9D3]/40 mb-5">
        <Search size={22} className="text-[#E8A6B8]" strokeWidth={1.6} />
      </div>
      <h3 className="font-serif-display text-3xl text-[#3A3A3A] leading-tight">
        Nothing here <span className="italic text-[#E8A6B8]">just yet</span>
      </h3>
      <p className="mt-4 text-sm text-[#5C5C5C] leading-relaxed">
        {query ? (
          <>No articles matched <strong>"{query}"</strong>. Try a different keyword or browse all articles.</>
        ) : (
          <>This category is still being filled. Check back soon — new posts are added regularly.</>
        )}
      </p>
    </div>
  );
}

/* ---------- AUTHOR CARD ---------- */
function AuthorCard() {
  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <article className="grid md:grid-cols-12 gap-8 items-center bg-[#F6EFE9] rounded-[2rem] border border-[#F3C9D3]/40 p-7 sm:p-10">
          <div className="md:col-span-4">
            <div className="relative rounded-[1.75rem] overflow-hidden aspect-[4/5] bg-[#F3C9D3]/40">
              <img
                src={ABOUT.image}
                alt="Maya — women's strength coach London, HerFitHub author"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-8">
            <p className="eyebrow mb-3">Written By</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A] leading-tight">
              Maya · <span className="italic text-[#E8A6B8]">HerFitHub</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-[#5C5C5C] leading-relaxed">
              London-based women's strength coach helping women build strength,
              confidence and trust in their bodies. Every article in this
              journal is written by Maya — drawn from years of coaching real
              women through real life.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/about" className="btn-outline text-xs" data-testid="author-about-link">
                Meet Maya <ArrowRight size={13} />
              </Link>
              <a
                href={`mailto:${BRAND.email}`}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] hover:text-[#E8A6B8] transition px-3 py-2"
                aria-label="Email Maya"
              >
                <Mail size={13} className="text-[#E8A6B8]" /> Email
              </a>
              {BRAND.instagram?.[0] && (
                <a
                  href={BRAND.instagram[0].url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] hover:text-[#E8A6B8] transition px-3 py-2"
                  aria-label="Maya on Instagram"
                >
                  <Instagram size={13} className="text-[#E8A6B8]" /> Instagram
                </a>
              )}
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

/* ---------- NEWSLETTER ---------- */
function NewsletterSection() {
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <SubscribeForm
          source="blog"
          title="Get new journal posts delivered with care"
          body="Fresh writing on women's strength training, postpartum recovery, gym confidence and the mindset shifts that change everything. Soft inbox, never spam — unsubscribe anytime."
        />
      </div>
    </section>
  );
}
